import { useEffect, useState } from "react";
import { useGetEnabledLocales, type Locale } from "src/api/locales/useLocales";
import { useLanguage } from "src/context/LanguageProvider";

/**
 * Hook pour détecter si la direction courante est RTL et fournir les styles adaptés
 */
export const useRTLLayout = () => {
    const { currentLanguage } = useLanguage();
    const { data: enabledLocales } = useGetEnabledLocales();
    const [isRTL, setIsRTL] = useState(false);

    useEffect(() => {
        if (!enabledLocales || !currentLanguage) {
            setIsRTL(false);
            return;
        }

        const currentLocale = enabledLocales.find((locale: Locale) => locale.code === currentLanguage);
        const direction = currentLocale?.direction || "ltr";
        setIsRTL(direction === "rtl");
    }, [currentLanguage, enabledLocales]);

    /**
     * Retourne le style de marge approprié pour le contenu principal
     * en fonction de la direction et de l'état de la sidebar
     */
    const getContentMargin = (sidebarWidth: string, isMobile: boolean = false) => {
        if (isMobile) {
            return { marginLeft: "0px", marginRight: "0px" };
        }

        if (isRTL) {
            return {
                marginLeft: "0px",
                marginRight: sidebarWidth,
            };
        } else {
            return {
                marginLeft: sidebarWidth,
                marginRight: "0px",
            };
        }
    };

    /**
     * Retourne la direction de la stack/flex pour les éléments qui doivent s'adapter au RTL
     */
    const getFlexDirection = (defaultDirection: "row" | "column" = "row") => {
        if (defaultDirection === "column") return "column";
        return isRTL ? "row-reverse" : "row";
    };

    return {
        isRTL,
        getContentMargin,
        getFlexDirection,
    };
};
